import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Information } from "@/components/Information/Information";
import { FileDropzone } from "@/components";

import {
	useLazyDownloadCsvEnrichmentQuery,
	useResetCsvEnrichmentMutation,
	useUpdateCsvEnrichmentFileMutation,
} from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { classNames } from "@/utils/classNames";
import { runOrToast } from "@/utils/runOrToast";

import { List } from "../../schema/list";
import {
	CreateCsvEnrichment,
	CreateCsvEnrichmentSchema,
} from "../../../CsvEnrichment/schema/csvEnrichment";
import { canResetToOriginalFile } from "./canResetToOriginalFile";

export interface ProvidedFileCardProps {
	list: List;
}

/**
 * "Review provided file": lets the user download the file we provided for a scraping request and
 * optionally replace it with an altered version. The delivery history itself lives in
 * `ScrapedFilesCard` (PREC-3091).
 */
export const ProvidedFileCard: FunctionComponent<ProvidedFileCardProps> = ({ list }) => {
	const { t } = useTranslation();
	const [updateCsvEnrichment, { isLoading }] = useUpdateCsvEnrichmentFileMutation();

	const [resetCsvEnrichment, { isLoading: resetCsvEnrichmentIsLoading }] =
		useResetCsvEnrichmentMutation();

	const [download, { isLoading: downloadIsLoading }] = useLazyDownloadCsvEnrichmentQuery();

	const [reviewProvidedFile, setReviewProvidedFile] = useState<boolean>(false);
	const [currentFile, setCurrentFile] = useState<File | null>(null);

	const [fileContent, setFileContent] = useState<{
		file: string;
		id: number;
	}>();

	const { handleSubmit, setValue, getValues } = useForm<CreateCsvEnrichment>({
		defaultValues: {
			name: "",
			file: "",
			webhook: false,
		},
		resolver: zodResolver(CreateCsvEnrichmentSchema),
	});

	const onSubmit = async (values: CreateCsvEnrichment) => {
		try {
			const { file: fileRaw } = values;

			const formData = new FormData();

			if (fileRaw) {
				const res: Response = await fetch(fileRaw);
				const blob: Blob = await res.blob();
				const file = new File([blob], currentFile?.name || "file.csv");

				formData.append("file", file);
			}

			formData.append("id", fileContent?.id.toString() || "");
			formData.append("type", "xlsx");
			formData.append("webhook", values.webhook.toString());

			if (fileContent) {
				await updateCsvEnrichment({
					form: formData,
					listId: list.id,
					id: fileContent.id,
				}).unwrap();
			}
		} catch (err) {
			console.error(err);
		}
	};

	const handleDownload = (id: number, name: string) => async () => {
		const blob = await download(id).unwrap();

		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");

		a.href = url;
		a.download = `${name.replaceAll(".xlsx", "")}.xlsx`;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	};

	// PREC-4392: a refused reset is shown, with the reason the API gave.
	const handleReset = async (id: number) => {
		await runOrToast(
			() => resetCsvEnrichment({ id, listId: list.id }).unwrap(),
			t("errors.fileResetFailed")
		);
	};

	const handleDrop = async (files: File[]): Promise<void> => {
		if (!files.length) {
			return;
		}

		setFileContent({
			file: URL.createObjectURL(files[0]),
			id: list.csvEnrichments?.[0]?.id || 0,
		});

		setCurrentFile(files[0]);

		if (!getValues("name")) {
			setValue("name", files[0].name, {
				shouldValidate: true,
				shouldDirty: true,
			});
		}

		setValue("file", URL.createObjectURL(files[0]));
	};

	return (
		<div
			className={classNames(
				"border p-6 w-full items-start rounded-xl border-1 border-border-light",
				"flex flex-col text-left gap-6",
				reviewProvidedFile ? "bg-background-light" : "bg-white"
			)}
		>
			<button
				className={classNames(
					"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
				)}
				onClick={() => {
					setReviewProvidedFile(!reviewProvidedFile);
				}}
			>
				<div className="grow">
					<div className="text-smd text-text font-bold">Review provided file (Optional)</div>
					<div className="text-ssm text-text-meta">
						The file is available and has{" "}
						{list?.csvEnrichments?.[0]?.originalCsvEnrichment?.amountInCsv} records. If you want you
						can adjust it be removing records that are not needed.
					</div>
				</div>
				<div
					className={classNames(
						"flex min-w-[36px] !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
						reviewProvidedFile
							? "border-border-light bg-white"
							: "border-transparent bg-background-light"
					)}
				>
					<Icon
						className="text-text-meta group-hover:text-white w-[12px] h-auto"
						icon={reviewProvidedFile ? faChevronUp : faChevronDown}
					/>
				</div>
			</button>
			{reviewProvidedFile && (
				<>
					{list?.csvEnrichments?.map((csvEnrichment, index) => {
						return (
							<div
								key={`csvEnrichment-${index * 2}`}
								className="bg-white border flex flex-col gap-4 border-divider rounded-xl p-4 mt-4 w-full"
							>
								<div className="flex flex-col gap-4">
									{csvEnrichment.originalCsvEnrichmentId && (
										<div className="mb-4 flex flex-col grow">
											<div className="w-full bg-white rounded-xl text-sm mb-2">
												Original file includes {csvEnrichment.originalCsvEnrichment?.amountInCsv}{" "}
												records
											</div>
											<Button
												color={ButtonColor.ACTION}
												isLoading={downloadIsLoading}
												title={`Download ${csvEnrichment.name} file`}
												onClick={handleDownload(
													csvEnrichment.originalCsvEnrichmentId,
													csvEnrichment.name
												)}
											/>
										</div>
									)}
								</div>

								{!csvEnrichment.altered && (
									<>
										<div className="w-full bg-white rounded-xl text-sm">
											To optimize your results, you can upload a new file{" "}
											<Information
												information={"Do not remove any columns or change any column names"}
											/>
										</div>

										<FileDropzone
											accept={{
												"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
													".xlsx",
												],
											}}
											caption={currentFile ? currentFile.name : ""}
											maxFiles={1}
											onDrop={handleDrop}
										/>

										<Button
											color={ButtonColor.ACTION}
											disabled={fileContent?.id !== csvEnrichment.id}
											isLoading={isLoading}
											title={`Upload my file`}
											onClick={handleSubmit(onSubmit)}
										/>
									</>
								)}
								{csvEnrichment.altered && (
									<div className="flex flex-col grow">
										<div className="w-full bg-white rounded-xl text-sm mb-2">
											You uploaded an altered version of the initial Excel file. Your version
											includes {csvEnrichment.amountInCsv} records.
										</div>
										<div className="flex flex-row gap-4 grow">
											<div className="flex flex-row gap-4 grow">
												<Button
													color={ButtonColor.ACTION_SECONDARY}
													isLoading={downloadIsLoading}
													title={`Download my file`}
													onClick={handleDownload(csvEnrichment.id, csvEnrichment.name)}
												/>
											</div>
											{canResetToOriginalFile(csvEnrichment) && (
												<div className="w-auto">
													<Button
														color={ButtonColor.DANGER_SECONDARY}
														isLoading={resetCsvEnrichmentIsLoading}
														title={`Reset to original`}
														onClick={() => handleReset(csvEnrichment.id)}
													/>
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						);
					})}
				</>
			)}
		</div>
	);
};
